import React, { useRef, useEffect, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ChatMessage } from '../ChatComponents';
import {
  LoadMoreArchiveButton,
  ThreadSummaryCard,
  isFirstActiveMessage,
} from '../ChatComponents';
import { Bot } from 'lucide-react';

interface VirtualizedMessageListProps {
  messages: ChatMessage[];
  hasMoreArchive: boolean;
  loadingArchive: boolean;
  onLoadMoreArchive: () => void;
  onCollapseArchive?: () => void;
  threadSummary: string | null;
  summaryExpanded: boolean;
  onToggleSummary: () => void;
  renderMessage: (msg: ChatMessage, msgIdx: number) => React.ReactNode;
  /** Auto-scroll to bottom when new messages arrive */
  shouldAutoScroll: boolean;
  /** Empty state placeholder text lines */
  emptyTitle?: string;
  emptySubtitle?: string;
}

/**
 * Message list with virtualization for long threads.
 *
 * Items layout (flattened):
 *   0: LoadMoreArchiveButton (if hasMoreArchive)
 *   1: Collapse archive button (if any archive messages exist)
 *   2..N: Message items (with ThreadSummaryCard rendered inline before first active message)
 *
 * Short threads render in plain document flow (no virtualization). Virtualizing a
 * dynamic-height list places items by an `estimateSize` guess and only corrects the
 * absolute offsets once `measureElement` (ResizeObserver) reports the real height —
 * on first paint, before that correction, a message taller than the estimate is
 * overlapped by the next item (the classic "messages overlap until you scroll" bug).
 * Plain flow can't overlap, so we only pay the virtualization cost (and accept its
 * first-paint reflow) for genuinely long threads where it matters for perf.
 */
const VIRTUALIZE_THRESHOLD = 80;

const VirtualizedMessageList: React.FC<VirtualizedMessageListProps> = ({
  messages,
  hasMoreArchive,
  loadingArchive,
  onLoadMoreArchive,
  onCollapseArchive,
  threadSummary,
  summaryExpanded,
  onToggleSummary,
  renderMessage,
  shouldAutoScroll,
  emptyTitle = '대화 내역이 없습니다',
  emptySubtitle,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(messages.length);
  const prevFirstMsgIdRef = useRef<string | null>(messages[0]?.id ?? null);
  const isArchiveLoadRef = useRef(false);

  // Build flat item list
  const hasArchiveMessages = messages.some((m) => m.source === 'archive');

  // Header items count (before messages)
  const headerCount = (hasMoreArchive ? 1 : 0) + (hasArchiveMessages ? 1 : 0);
  const totalCount = headerCount + messages.length;

  // Only virtualize long threads — short ones use plain flow (no overlap, see header note).
  const shouldVirtualize = totalCount > VIRTUALIZE_THRESHOLD;

  const virtualizer = useVirtualizer({
    count: totalCount,
    getScrollElement: () => scrollRef.current,
    // Closer to a real markdown turn than the old 120 → less first-paint reflow/overlap.
    estimateSize: () => 220,
    overscan: 6,
  });

  // Detect prepend (archive load) vs append (new message) and keep the view anchored.
  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const prevFirstId = prevFirstMsgIdRef.current;
    const currentFirstId = messages[0]?.id ?? null;
    const isPrepend =
      messages.length > prevCount && currentFirstId !== prevFirstId && prevFirstId !== null;
    const isAppend = messages.length > prevCount && !isPrepend;

    if (isPrepend) {
      if (shouldVirtualize) {
        const oldFirstIdx = messages.findIndex((m) => m.id === prevFirstId);
        if (oldFirstIdx > 0) {
          isArchiveLoadRef.current = true;
          virtualizer.scrollToIndex(headerCount + oldFirstIdx, { align: 'start' });
        }
      }
      // Plain flow: leave the scroll position as-is (browser keeps the offset from top;
      // a small shift on explicit "load more" is acceptable and avoids a jump-to-bottom).
    } else if (isAppend && shouldAutoScroll) {
      if (shouldVirtualize) {
        virtualizer.scrollToIndex(totalCount - 1, { align: 'end', behavior: 'smooth' });
      } else {
        // Instant on first load (prevCount === 0), smooth for subsequent sends.
        bottomRef.current?.scrollIntoView({ behavior: prevCount === 0 ? 'auto' : 'smooth' });
      }
    }

    prevMessageCountRef.current = messages.length;
    prevFirstMsgIdRef.current = currentFirstId;
  }, [messages, totalCount, headerCount, shouldAutoScroll, shouldVirtualize]);

  // Reset archive load flag after scroll
  useEffect(() => {
    if (isArchiveLoadRef.current) {
      requestAnimationFrame(() => {
        isArchiveLoadRef.current = false;
      });
    }
  });

  const renderItem = useCallback(
    (index: number) => {
      let headerIdx = 0;

      // LoadMoreArchiveButton
      if (hasMoreArchive) {
        if (index === headerIdx) {
          return <LoadMoreArchiveButton loading={loadingArchive} onClick={onLoadMoreArchive} />;
        }
        headerIdx++;
      }

      // Collapse archive button
      if (hasArchiveMessages) {
        if (index === headerIdx) {
          return (
            <button
              onClick={onCollapseArchive}
              className="mx-auto flex items-center gap-1.5 px-3 py-1.5 text-xs text-slate-500 bg-slate-100 rounded-full hover:bg-slate-200 transition-colors"
            >
              이전 대화 접기
            </button>
          );
        }
        headerIdx++;
      }

      // Message item
      const msgIdx = index - headerIdx;
      const msg = messages[msgIdx];
      if (!msg) return null;

      return (
        <>
          {threadSummary && isFirstActiveMessage(messages, msgIdx) && (
            <ThreadSummaryCard
              summary={threadSummary}
              expanded={summaryExpanded}
              onToggle={onToggleSummary}
            />
          )}
          {renderMessage(msg, msgIdx)}
        </>
      );
    },
    [
      messages,
      hasMoreArchive,
      loadingArchive,
      onLoadMoreArchive,
      hasArchiveMessages,
      onCollapseArchive,
      threadSummary,
      summaryExpanded,
      onToggleSummary,
      renderMessage,
    ],
  );

  if (messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
        <div className="text-center text-slate-400 py-16">
          <Bot size={48} className="mx-auto mb-3 opacity-50" />
          <p>{emptyTitle}</p>
          {emptySubtitle && <p className="text-xs mt-2">{emptySubtitle}</p>}
        </div>
      </div>
    );
  }

  // Plain document flow for short threads — no absolute positioning, so no overlap.
  if (!shouldVirtualize) {
    return (
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
        {Array.from({ length: totalCount }, (_, i) => (
          <div key={i} className="pb-4">
            {renderItem(i)}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
    );
  }

  return (
    <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => (
          <div
            key={virtualRow.key}
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${virtualRow.start}px)`,
            }}
          >
            {/* Preserve space-y-4 gap (16px) via padding on each item */}
            <div className="pb-4">
              {renderItem(virtualRow.index)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VirtualizedMessageList;
